import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, X, ChevronRight, Car, Eye, Sun, Thermometer, Palette, Wifi, Award, Moon, Sparkles, Flame, Shield, Gem } from 'lucide-react';
import { pricing } from '../data/mock';
import { useNavigate } from 'react-router-dom';
import DiscountedPrice, { usePromo } from './DiscountedPrice';

const StarlightTwinkle = () => {
  const stars = useMemo(() => {
    const s = [];
    for (let i = 0; i < 45; i++) {
      s.push({
        left: `${8 + Math.random() * 84}%`,
        top: `${5 + Math.random() * 45}%`,
        size: 1.5 + Math.random() * 2,
        delay: Math.random() * 3,
        duration: 0.8 + Math.random() * 1.2,
      });
    }
    return s;
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
      <style>{`
        @keyframes starTwinkle {
          0%, 100% { opacity: 0.15; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
      {stars.map((star, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: star.left,
            top: star.top,
            width: star.size,
            height: star.size,
            animation: `starTwinkle ${star.duration}s ${star.delay}s ease-in-out infinite`,
            boxShadow: '0 0 4px 1px rgba(255,255,255,0.6)',
          }}
        />
      ))}
    </div>
  );
};

const premiumCarbonServices = [
  { id: 'pc-sedan', title: 'Sedan & Coupe', price: 200, description: 'Sides and Rear Only', features: ['99% UV Rejection', 'Blocks Heat', 'Never Fades', 'Signal-Safe', 'Reduces Glare', 'Lifetime Warranty'] },
  { id: 'pc-sedan-full', title: 'Sedan & Coupe Full', price: 300, description: 'ALL Windows incl. Front Windshield', features: ['99% UV Rejection', 'Blocks Heat', 'Never Fades', 'Signal-Safe', 'Reduces Glare', 'Lifetime Warranty'] },
  { id: 'pc-truck', title: 'Trucks & SUV', price: 300, description: 'Sides and rear', features: ['99% UV Rejection', 'Blocks Heat', 'Never Fades', 'Signal-Safe', 'Reduces Glare', 'Lifetime Warranty'] },
  { id: 'pc-truck-full', title: 'Trucks & SUV Full', price: 400, description: 'ALL Windows incl. Front Windshield', features: ['99% UV Rejection', 'Blocks Heat', 'Never Fades', 'Signal-Safe', 'Reduces Glare', 'Lifetime Warranty'] },
  { id: 'pc-windshield', title: 'Front Windshield Only', price: 100, description: 'Full windshield tint', features: ['99% UV Rejection', 'Blocks Heat', 'Never Fades', 'Signal-Safe', 'Reduces Glare', 'Lifetime Warranty'] },
  { id: 'pc-front2', title: 'Front Two Windows', price: 100, description: 'Match rear factory', features: ['99% UV Rejection', 'Blocks Heat', 'Never Fades', 'Signal-Safe', 'Reduces Glare', 'Lifetime Warranty'] },
];

const ceramicTintServices = [
  { id: 'ct-sedan', title: 'Sedan & Coupe', price: 300, description: 'Sides and Rear Only', image: '/images/ceramic-sedan.jpeg', features: ['Up to 65% Heat Rejection', '99% UV & IR Block', 'Crystal Night Clarity', 'Zero Signal Loss', 'Non-Metallic, No Fade', 'Lifetime Warranty'] },
  { id: 'ct-sedan-full', title: 'Sedan & Coupe Full', price: 400, description: 'ALL Windows incl. Front Windshield', image: '/images/vehicles/truck-full.png', features: ['Up to 65% Heat Rejection', '99% UV & IR Block', 'Crystal Night Clarity', 'Zero Signal Loss', 'Non-Metallic, No Fade', 'Lifetime Warranty'] },
  { id: 'ct-truck', title: 'Trucks & SUV', price: 400, description: 'Sides and rear', image: '/images/ceramic-truck-suv.jpeg', features: ['Up to 65% Heat Rejection', '99% UV & IR Block', 'Crystal Night Clarity', 'Zero Signal Loss', 'Non-Metallic, No Fade', 'Lifetime Warranty'] },
  { id: 'ct-truck-full', title: 'Trucks & SUV Full', price: 500, description: 'ALL Windows incl. Front Windshield', image: '/images/vehicles/sedan-full.png', features: ['Up to 65% Heat Rejection', '99% UV & IR Block', 'Crystal Night Clarity', 'Zero Signal Loss', 'Non-Metallic, No Fade', 'Lifetime Warranty'] },
  { id: 'ct-windshield', title: 'Front Windshield Only', price: 150, description: 'Full windshield tint', image: '/images/ceramic-windshield.jpeg', features: ['Up to 65% Heat Rejection', '99% UV & IR Block', 'Crystal Night Clarity', 'Zero Signal Loss', 'Non-Metallic, No Fade', 'Lifetime Warranty'] },
  { id: 'ct-front2', title: 'Front Two Windows', price: 150, description: 'Match rear factory', image: '/images/ceramic-front-windows.jpeg', features: ['Up to 65% Heat Rejection', '99% UV & IR Block', 'Crystal Night Clarity', 'Zero Signal Loss', 'Non-Metallic, No Fade', 'Lifetime Warranty'] },
];

// Vehicle combined image (top = rear 3/4, bottom = side profile — both views in one image)
const vehicleImagePairs = {
  'pc-sedan':      '/images/vehicles/sedan-carbon.png',
  'pc-sedan-full': '/images/vehicles/truck-full.png',
  'ct-sedan':      '/images/vehicles/sedan-ceramic.png',
  'ct-sedan-full': '/images/vehicles/truck-full.png',
  'pc-truck':      '/images/vehicles/truck-carbon.png',
  'pc-truck-full': '/images/vehicles/sedan-full.png',
  'ct-truck':      '/images/vehicles/truck-ceramic.png',
  'ct-truck-full': '/images/vehicles/sedan-full.png',
  'pc-windshield': '/images/vehicles/windshield-carbon.png',
  'ct-windshield': '/images/vehicles/windshield-ceramic.png',
  'pc-front2':     '/images/vehicles/front2-carbon.png',
  'ct-front2':     '/images/vehicles/front2-ceramic.png',
};

// Icon mapping for feature chips
const featureIconMap = {
  '99% UV Rejection': Sun,
  'Blocks Heat': Thermometer,
  'Never Fades': Palette,
  'Signal-Safe': Wifi,
  'Reduces Glare': Eye,
  'Lifetime Warranty': Award,
  'Up to 65% Heat Rejection': Flame,
  '99% UV & IR Block': Sun,
  'Crystal Night Clarity': Moon,
  'Zero Signal Loss': Wifi,
  'Non-Metallic, No Fade': Sparkles,
};

const windowTintServices = [...premiumCarbonServices];

const starlightServices = [
  { id: 'starlight-1', title: 'Starlight 1', price: 800, description: '450 Stars — $800', features: ['450 fiber optic stars', 'App controlled', 'Adjustable brightness', 'Premium materials'] },
  { id: 'starlight-2', title: 'Starlight 2', price: 950, description: '550 Stars — $950', features: ['550 fiber optic stars', 'App controlled', 'Adjustable brightness', 'Premium materials'] },
  { id: 'starlight-3', title: 'Starlight 3', price: 1000, description: '700 Stars — $1,000', features: ['700 fiber optic stars', 'App controlled', 'Adjustable brightness', 'Premium materials'] },
  { id: 'starlight-4', title: 'Starlight 4', price: 1200, description: '1,000 Stars — $1,200', features: ['1,000 fiber optic stars', 'App controlled', 'Adjustable brightness', 'Premium materials'] }
];

const vinylWrapServices = [
  { id: 'vinyl-full', title: 'Full Body Wrap', price: 'Contact for quote', description: 'Complete vehicle vinyl wrap', features: ['Full exterior coverage', 'Color change options', 'Paint protection', 'Premium vinyl material'] },
  { id: 'vinyl-partial', title: 'Partial Wrap', price: 'Contact for quote', description: 'Targeted vinyl wrap sections', features: ['Hood, roof, or trunk', 'Accent pieces', 'Custom designs', 'Premium vinyl material'] }
];

const ambientServices = pricing.filter(p => p.title === 'Ambient Lighting');

const tintRemovalServices = [
  { id: 'tr-full', title: 'Fix-It Ticket', price: 150, description: 'Sides + rear removal', image: '/images/tint-removal.jpeg', features: ['Full Tint Strip', 'Adhesive Removal', 'Window Cleaning', 'Same Day Service'] },
  { id: 'tr-windshield', title: 'Front Windshield Only', price: 100, description: 'Windshield removal', image: '/images/tint-removal.jpeg', features: ['Full Tint Strip', 'Adhesive Removal', 'Window Cleaning', 'Same Day Service'] },
  { id: 'tr-front2', title: 'Front Two Windows Only', price: 100, description: 'Driver + Passenger', image: '/images/tint-removal.jpeg', features: ['Full Tint Strip', 'Adhesive Removal', 'Window Cleaning', 'Same Day Service'] },
  { id: 'tr-full-vehicle', title: 'Full Vehicle Removal', price: 250, description: 'All windows + windshield', image: '/images/tint-removal.jpeg', features: ['Full Tint Strip', 'Adhesive Removal', 'All Glass Cleaned', 'Same Day Service'] },
];

const windowTintImages = {
  'Front Windshield Only': '/images/windshield.jpeg',
  'Front Two Windows': '/images/front-windows.jpeg',
  'Sedan & Coupe': '/images/sedan-coupe.jpeg',
  'Trucks & SUV': '/images/truck-suv.jpeg',
};

const chromeDeleteServices = [
  { id: 'cd-trim', title: 'Window Trim Delete', price: 200, description: 'Both Sides', features: ['All window trim', 'Both sides coverage', 'Premium vinyl material', 'Clean factory look'] },
  { id: 'cd-handles', title: 'Door Handles', price: 150, description: '4 Doors', features: ['All 4 door handles', 'Precision cut vinyl', 'Seamless finish', 'Durable material'] },
  { id: 'cd-emblem', title: 'Front & Rear Emblem', price: 100, description: 'Badges', features: ['Front emblem', 'Rear emblem', 'Precision wrap', 'Clean blackout look'] },
  { id: 'cd-special', title: 'Special Request', price: 'Contact Us', description: 'Custom Work', features: ['Custom chrome delete', 'Tailored design', 'Premium materials', 'Professional install'] },
];

const categories = [
  { id: 'window-tint', title: 'Window Tint', num: '01', desc: 'Professional automotive window tinting.', image: '/images/tint-install.jpeg', services: [...premiumCarbonServices], displayCount: 12, showImages: true },
  { id: 'starlight', title: 'Starlight', num: '02', desc: 'Luxury starlight headliner installation.', image: '/images/starlight-venom.jpeg', services: starlightServices, showImages: false },
  { id: 'vinyl-wrap', title: 'Vinyl Wrap', num: '03', desc: 'Complete or partial vehicle wraps.', image: '/images/bmw-front.jpeg', services: vinylWrapServices, showImages: false },
  { id: 'ambient', title: 'Ambient Lights', num: '04', desc: 'Custom interior LED lighting.', image: '/images/ambient-pro.jpeg', services: ambientServices, showImages: false },
  { id: 'chrome-delete', title: 'Chrome Delete', num: '05', desc: 'Chrome trim blackout service.', image: '/images/chrome-delete.jpeg', services: chromeDeleteServices, showImages: false },
  { id: 'tint-removal', title: 'Tint Removal', num: '06', desc: 'Professional tint film removal.', image: '/images/tint-removal-tab.jpg', services: tintRemovalServices, showImages: false },
];

export const PricingSection = ({ onBookNow, externalCategory, onCategoryChange }) => {
  const [internalCategory, setInternalCategory] = useState(null);
  const [selectedService, setSelectedService] = useState('');
  const [showServiceModal, setShowServiceModal] = useState(false);
  const navigate = useNavigate();

  const activeCategory = externalCategory !== undefined && externalCategory !== null ? externalCategory : internalCategory;

  const setActiveCategory = (catId) => {
    setInternalCategory(catId);
    if (onCategoryChange) onCategoryChange(catId);
  };

  const activeCat = categories.find(c => c.id === activeCategory);

  const handleCardClick = (catId) => {
    if (catId === 'vinyl-wrap') {
      setActiveCategory(null);
      setSelectedService('');
      setShowServiceModal(false);
      navigate('/wrap-designer');
      return;
    }
    if (catId === 'starlight') {
      navigate('/starlights');
      return;
    }
    if (catId === 'ambient') {
      navigate('/ambient-lighting');
      return;
    }
    if (catId === 'chrome-delete') {
      navigate('/chrome-delete');
      return;
    }
    if (catId === 'tint-removal') {
      navigate('/tint-removal');
      return;
    }
    if (activeCategory === catId) {
      setActiveCategory(null);
      setSelectedService('');
      setShowServiceModal(false);
    } else {
      setActiveCategory(catId);
      setSelectedService('');
      const cat = categories.find(c => c.id === catId);
      if (cat && cat.services.length > 1) {
        setShowServiceModal(true);
      } else if (cat && cat.services.length === 1) {
        setShowServiceModal(false);
        setSelectedService(cat.services[0].title);
      }
    }
  };

  // Show service modal when Window Tint is selected
  React.useEffect(() => {
    if (activeCategory === 'window-tint') {
      setShowServiceModal(true);
    }
  }, [activeCategory]);

  const handleBookNow = () => {
    if (onBookNow) {
      const categoryTitle = activeCat?.title || '';
      // Find the active service across all tiers
      const allTintServices = [...premiumCarbonServices, ...ceramicTintServices, ...tintRemovalServices];
      const activeService = activeCat?.id === 'window-tint'
        ? allTintServices.find(s => s.id === selectedService || s.title === selectedService)
        : activeCat?.services.find(s => s.title === selectedService);
      const serviceName = activeService?.title || selectedService || '';
      const serviceTypeMap = {
        'Window Tint': 'Window Tinting',
        'Tint Removal': 'Remove Tint',
        'Starlight': 'Starlight Headliner',
        'Vinyl Wrap': 'Vinyl Wrap',
        'Ambient Lights': 'Ambient Lighting',
        'Ambient Lighting': 'Ambient Lighting',
        'Remove Tint': 'Remove Tint',
        'Chrome Delete': 'Chrome Delete',
      };
      // Build package name with tier info for window tint
      let packageName = serviceName;
      if (activeCat?.id === 'window-tint' && activeService) {
        const isCeramic = ceramicTintServices.some(s => s.id === activeService.id);
        const isPremium = premiumCarbonServices.some(s => s.id === activeService.id);
        if (isCeramic) packageName = `${serviceName} (Ceramic)`;
        else if (isPremium) packageName = `${serviceName} (Premium Carbon)`;
      }
      // Close the details popup before navigating
      setSelectedService('');
      onBookNow({
        service: serviceTypeMap[categoryTitle] || categoryTitle,
        package: packageName,
      });
    }
  };

  const categoryToDiscountKey = {
    'window-tint': 'Window Tinting',
    'chrome-delete': 'Chrome Delete',
    'starlight': 'Starlight Headliner',
    'vinyl-wrap': 'Vinyl Wrap',
    'ambient': 'Ambient Lighting',
    'tint-removal': 'Remove Tint',
  };

  const renderCard = (cat, cardHeight = '78px', compact = false) => {
    const isActive = activeCategory === cat.id;
    const isFaded = activeCategory && !isActive;
    const count = cat.displayCount ?? cat.services.length;
    const optionText = `${count} option${count !== 1 ? 's' : ''}`;
    return (
      <button
        key={cat.id}
        data-testid={`category-${cat.id}`}
        onClick={() => handleCardClick(cat.id)}
        className="group relative w-full overflow-hidden text-left service-tab"
        data-active={isActive ? 'true' : 'false'}
        style={{
          height: cardHeight,
          borderRadius: '16px',
          border: isActive ? '2px solid #eab308' : '1px solid rgba(255,255,255,0.10)',
          boxShadow: isActive
            ? '0 0 32px rgba(234,179,8,0.60), 0 24px 48px rgba(0,0,0,0.85), 0 8px 20px rgba(0,0,0,0.5)'
            : '0 10px 22px rgba(0,0,0,0.55), 0 2px 4px rgba(0,0,0,0.4)',
          transform: isActive ? 'scale(1.05) translateY(-5px)' : 'scale(1) translateY(0)',
          opacity: isFaded ? 0.20 : 1,
          pointerEvents: isFaded ? 'none' : 'auto',
          zIndex: isActive ? 10 : 1,
          position: 'relative',
        }}
      >
        {/* Background image */}
        <img
          src={cat.image}
          alt={cat.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {cat.id === 'starlight' && <StarlightTwinkle />}

        {/* Dark overlay — uniform so centered text reads clearly */}
        <div
          className="absolute inset-0"
          style={{ background: 'rgba(0,0,0,0.50)' }}
        />

        {/* Top-edge sheen */}
        <div
          className="absolute inset-x-0 top-0"
          style={{ height: '100%', background: 'linear-gradient(180deg, rgba(255,255,255,0.13) 0%, transparent 28%)', pointerEvents: 'none' }}
        />

        {/* Centered title */}
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <span
            className="font-black uppercase leading-none"
            style={{
              color: '#ffffff',
              fontSize: compact ? '15px' : '17px',
              letterSpacing: compact ? '0.8px' : '1.5px',
              textShadow: '0px 2px 6px rgba(0,0,0,1)',
              textAlign: 'center',
            }}
          >
            {cat.title}
          </span>
        </div>

        {/* Options count — bottom */}
        <div className="absolute bottom-2 left-0 right-0 flex justify-center z-10">
          <span style={{ color: '#a1a1aa', fontSize: '11px' }}>
            {optionText}
          </span>
        </div>

      </button>
    );
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="bg-black relative" style={{ paddingTop: '12px', paddingBottom: '8px', zIndex: 2 }}>
      <style>{`
        .service-tab {
          transition: box-shadow 0.25s ease, border-color 0.25s ease,
                      transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
                      opacity 0.2s ease;
        }
        .service-tab:hover:not([data-active="true"]) {
          box-shadow: 0 0 22px rgba(234,179,8,0.45), 0 10px 28px rgba(0,0,0,0.6) !important;
          border-color: rgba(234,179,8,0.55) !important;
        }
      `}</style>
      <div className="max-w-lg mx-auto px-4 sm:px-6 relative z-10">

        {/* Service tab grid */}
        <div className="flex flex-col gap-3">
          {/* Row 1: Window Tint — hero, full width */}
          {renderCard(categories.find(c => c.id === 'window-tint'), '90px', false)}

          {/* Row 2: Starlight (left) | Ambient Lights (right) — equal height */}
          <div className="grid grid-cols-2 gap-3" style={{ overflow: 'visible' }}>
            {renderCard(categories.find(c => c.id === 'starlight'), '110px', true)}
            {renderCard(categories.find(c => c.id === 'ambient'), '110px', true)}
          </div>

          {/* Row 3: Vinyl Wrap (left) | Chrome Delete (right) — equal height */}
          <div className="grid grid-cols-2 gap-3" style={{ overflow: 'visible' }}>
            {renderCard(categories.find(c => c.id === 'vinyl-wrap'), '110px', true)}
            {renderCard(categories.find(c => c.id === 'chrome-delete'), '110px', true)}
          </div>

          {/* Row 4: Tint Removal (left) | Check Status (right) */}
          <div className="grid grid-cols-2 gap-3" style={{ overflow: 'visible' }}>
            {renderCard(categories.find(c => c.id === 'tint-removal'), '110px', true)}

            {/* Check Status nav tab */}
            <button
              data-testid="nav-tab-check-status"
              onClick={() => navigate('/my-appointments')}
              className="group relative w-full overflow-hidden text-left service-tab"
              style={{
                height: '110px',
                borderRadius: '16px',
                border: '1px solid rgba(255,255,255,0.10)',
                boxShadow: '0 10px 22px rgba(0,0,0,0.55), 0 2px 4px rgba(0,0,0,0.4)',
              }}
            >
              <img src="/images/tint-rain-city.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.50)' }} />
              <div className="absolute inset-x-0 top-0" style={{ height: '100%', background: 'linear-gradient(180deg, rgba(255,255,255,0.13) 0%, transparent 28%)', pointerEvents: 'none' }} />
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <span className="font-black uppercase leading-none" style={{ color: '#ffffff', fontSize: '15px', letterSpacing: '0.8px', textShadow: '0px 2px 6px rgba(0,0,0,1)', textAlign: 'center' }}>
                  Check Status
                </span>
              </div>
              <div className="absolute bottom-2 left-0 right-0 flex justify-center z-10">
                <span style={{ color: '#a1a1aa', fontSize: '10px', textAlign: 'center' }}>Look up my booking</span>
              </div>
            </button>
          </div>

          {/* Row 5: Submit Review — full width */}
          <button
            data-testid="nav-tab-submit-review"
            onClick={() => navigate('/submit-review')}
            className="group w-full text-left"
            style={{
              background: '#0a0a0a',
              border: '1px solid rgba(234,179,8,0.45)',
              borderRadius: '16px',
              padding: '18px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              cursor: 'pointer',
              transition: 'border-color 0.2s, background 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(234,179,8,0.75)'; e.currentTarget.style.background = '#0f0f0f'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(234,179,8,0.45)'; e.currentTarget.style.background = '#0a0a0a'; }}
          >
            {/* Left gold accent bar */}
            <div style={{ width: '3px', alignSelf: 'stretch', background: '#eab308', borderRadius: '2px', flexShrink: 0 }} />
            {/* Text block */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: '3px', marginBottom: '6px' }}>
                {[1,2,3,4,5].map(i => (
                  <span key={i} style={{ color: '#eab308', fontSize: '15px', lineHeight: 1 }}>★</span>
                ))}
              </div>
              <p style={{ color: '#ffffff', fontWeight: 800, fontSize: '16px', letterSpacing: '0.05em', textTransform: 'uppercase', margin: 0, lineHeight: 1.2 }}>Submit a Review</p>
              <p style={{ color: '#71717a', fontSize: '13px', margin: '4px 0 0', fontWeight: 400 }}>Share your experience</p>
            </div>
            {/* Chevron */}
            <div style={{ color: '#eab308', fontSize: '20px', fontWeight: 700, flexShrink: 0 }}>›</div>
          </button>
        </div>

      </div>


      {/* Service Picker Modal — Window Tint 3-Section Compact Grid */}
      {showServiceModal && activeCat && activeCat.id === 'window-tint' && createPortal(
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-2" style={{zIndex: 9999}} onClick={() => { setShowServiceModal(false); setActiveCategory(null); }}>
          <div className="bg-zinc-950 border border-zinc-800/40 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl shadow-black/60" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between px-4 pt-3 pb-2">
              <h3 className="text-white text-2xl font-bold">Select Package</h3>
              <button onClick={() => { setShowServiceModal(false); setActiveCategory(null); }} data-testid="close-service-modal" className="w-9 h-9 rounded-full bg-zinc-700/60 flex items-center justify-center hover:bg-zinc-600 transition-colors mt-0.5">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="px-3 pb-3 space-y-2">
              {/* shade gradient constant */}
              {/* light 50% → dark 5%: rgba(185,185,185,.78) → rgba(8,8,8,1) */}

              {/* ── Carbon Film ── */}
              <div className="rounded-xl border border-zinc-700/70 p-2">
                <p className="text-white text-[11px] font-bold uppercase tracking-widest mb-1.5 text-center">Carbon Film</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { id: 'pc-sedan',      title: 'Sedan & Coupe',      price: 200, desc: 'Sides & Rear' },
                    { id: 'pc-sedan-full', title: 'Sedan & Coupe Full', price: 300, desc: 'All Windows' },
                    { id: 'pc-truck',      title: 'Trucks & SUV',       price: 300, desc: 'Sides & Rear' },
                    { id: 'pc-truck-full', title: 'Trucks & SUV Full',  price: 400, desc: 'All Windows' },
                  ].map((pkg) => (
                    <button
                      key={pkg.id}
                      data-testid={`service-option-${pkg.id}`}
                      onClick={() => { setSelectedService(pkg.id); setShowServiceModal(false); }}
                      style={{ height: '78px', position: 'relative', overflow: 'hidden' }}
                      className="rounded-xl flex flex-col items-center justify-center transition-all duration-150 border w-full bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]"
                    >
                      <span className="font-bold leading-tight text-center px-1" style={{ fontSize: '13px' }}>{pkg.title}</span>
                      <span className="text-zinc-500 mt-0.5" style={{ fontSize: '10px' }}>{pkg.desc}</span>
                      <span className="font-bold mt-1 text-white" style={{ fontSize: '17px' }}><DiscountedPrice price={pkg.price} category="Window Tinting" /></span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Ceramic Film ── */}
              <div className="rounded-xl border border-amber-500/80 p-2">
                <p className="text-amber-500 text-[11px] font-bold uppercase tracking-widest mb-1.5 text-center">Ceramic Film</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { id: 'ct-sedan',      title: 'Sedan & Coupe',      price: 300, desc: 'Sides & Rear' },
                    { id: 'ct-sedan-full', title: 'Sedan & Coupe Full', price: 400, desc: 'All Windows' },
                    { id: 'ct-truck',      title: 'Trucks & SUV',       price: 400, desc: 'Sides & Rear' },
                    { id: 'ct-truck-full', title: 'Trucks & SUV Full',  price: 500, desc: 'All Windows' },
                  ].map((pkg) => (
                    <button
                      key={pkg.id}
                      data-testid={`service-option-${pkg.id}`}
                      onClick={() => { setSelectedService(pkg.id); setShowServiceModal(false); }}
                      style={{ height: '78px', position: 'relative', overflow: 'hidden' }}
                      className="rounded-xl flex flex-col items-center justify-center transition-all duration-150 border w-full bg-zinc-900 border-zinc-700/50 hover:border-zinc-600 active:scale-[0.97]"
                    >
                      <span className="font-bold leading-tight text-white text-center px-1" style={{ fontSize: '13px' }}>{pkg.title}</span>
                      <span className="text-zinc-500 mt-0.5" style={{ fontSize: '10px' }}>{pkg.desc}</span>
                      <span className="font-bold mt-1 text-amber-500" style={{ fontSize: '17px' }}><DiscountedPrice price={pkg.price} category="Window Tinting" /></span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Factory Match / Windshield ── */}
              <div className="rounded-xl border border-zinc-700/70 p-2">
                <p className="text-white text-[11px] font-bold uppercase tracking-widest mb-1.5 text-center">Factory Match / Windshield</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { id: 'pc-front2',     title: 'Front Two Windows',    price: 100, desc: 'Carbon - Driver & Pass.' },
                    { id: 'pc-windshield', title: 'Front Windshield Only', price: 100, desc: 'Carbon - Windshield' },
                    { id: 'ct-front2',     title: 'Front Two Windows',    price: 150, desc: 'Ceramic - Driver & Pass.' },
                    { id: 'ct-windshield', title: 'Front Windshield Only', price: 150, desc: 'Ceramic - Windshield' },
                  ].map((pkg) => (
                    <button
                      key={pkg.id}
                      data-testid={`service-option-${pkg.id}`}
                      onClick={() => { setSelectedService(pkg.id); setShowServiceModal(false); }}
                      style={{ height: '78px', position: 'relative', overflow: 'hidden' }}
                      className="rounded-xl flex flex-col items-center justify-center transition-all duration-150 border w-full bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]"
                    >
                      <span className="font-bold leading-tight text-white text-center px-1" style={{ fontSize: '13px' }}>{pkg.title}</span>
                      <span className="text-zinc-500 mt-0.5" style={{ fontSize: '10px' }}>{pkg.desc}</span>
                      <span className="font-bold mt-1 text-white" style={{ fontSize: '17px' }}><DiscountedPrice price={pkg.price} category="Window Tinting" /></span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Service Picker Modal — Non-Window-Tint Categories (Original Grid) */}
      {showServiceModal && activeCat && activeCat.id !== 'window-tint' && createPortal(
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}} onClick={() => { setShowServiceModal(false); setActiveCategory(null); }}>
          <div className="bg-zinc-950 border border-zinc-800/40 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl shadow-black/60" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between px-6 pt-6 pb-2">
              <div>
                <h3 className="text-amber-500 text-2xl font-bold">{activeCat.title}</h3>
                <p className="text-zinc-500 text-sm mt-0.5">Select a package</p>
              </div>
              <button onClick={() => { setShowServiceModal(false); setActiveCategory(null); }} className="w-9 h-9 rounded-full bg-zinc-700/60 flex items-center justify-center hover:bg-zinc-600 transition-colors mt-1">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="px-5 pb-6 pt-3 grid grid-cols-2 gap-3">
              {activeCat.services.map((s, idx) => {
                const isLastOdd = activeCat.services.length % 2 !== 0 && idx === activeCat.services.length - 1;
                return (
                  <button
                    key={s.id}
                    data-testid={`service-option-${s.title.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => { setSelectedService(s.title); setShowServiceModal(false); }}
                    className={`py-5 px-3 rounded-xl text-center transition-all duration-150 border ${
                      selectedService === s.title
                        ? 'bg-amber-500 border-amber-400 text-black'
                        : 'bg-zinc-900 border-zinc-700/50 text-white hover:border-zinc-600 active:scale-[0.97]'
                    } ${isLastOdd ? 'col-span-2 mx-auto w-1/2' : ''}`}
                  >
                  <span className="block font-bold text-sm leading-tight">{s.title}</span>
                  {s.description && (
                    <span className={`block text-xs mt-1 ${selectedService === s.title ? 'text-black/60' : 'text-zinc-400'}`}>{s.description}</span>
                  )}
                  <span className={`block text-lg font-bold mt-1.5 ${selectedService === s.title ? 'text-black/70' : 'text-amber-500'}`}>
                    {typeof s.price === 'number'
                      ? <DiscountedPrice price={s.price} category={categoryToDiscountKey[activeCat?.id] || ''} />
                      : s.price}
                  </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Service Details Popup Modal */}
      {selectedService && activeCat && (() => {
        let svc = null;
        if (activeCat.id === 'window-tint') {
          const allWindowServices = [...premiumCarbonServices, ...ceramicTintServices, ...tintRemovalServices];
          svc = allWindowServices.find(s => s.id === selectedService) || allWindowServices.find(s => s.title === selectedService);
        } else {
          svc = activeCat.services.find(s => s.title === selectedService) || (activeCat.services.length === 1 ? activeCat.services[0] : null);
        }
        if (!svc) return null;

        const isCeramic = ceramicTintServices.some(s => s.id === svc.id);
        const isPremiumCarbon = premiumCarbonServices.some(s => s.id === svc.id);
        const isWindowTint = isCeramic || isPremiumCarbon;
        const vehicleImgs = vehicleImagePairs[svc.id];
        const svcImage = svc.image || windowTintImages[svc.title];

        const materialLabel = isCeramic ? 'CERAMIC TINT FILM' : isPremiumCarbon ? 'PREMIUM CARBON FILM' : null;
        const materialDesc = isCeramic
          ? 'Our top-tier nano-ceramic film — maximum heat rejection, crystal-clear visibility, and zero signal interference.'
          : 'A deep, non-fading black look with reliable heat and UV protection — the perfect balance of style, comfort, and value.';
        const materialBorder = isCeramic ? 'rgba(234,179,8,0.35)' : 'rgba(255,255,255,0.12)';
        const materialBg = isCeramic ? 'rgba(234,179,8,0.06)' : 'rgba(255,255,255,0.04)';
        const materialIconColor = isCeramic ? '#eab308' : '#ffffff';
        const MaterialIcon = isCeramic ? Gem : Shield;

        return createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 9999, background: 'rgba(0,0,0,0.90)', backdropFilter: 'blur(6px)' }}
            onClick={() => { setSelectedService(''); setShowServiceModal(true); }}
          >
            <div
              className="w-full"
              style={{ borderRadius: '20px', boxShadow: '0 24px 48px rgba(0,0,0,0.8)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}
              data-testid="category-details"
              onClick={e => e.stopPropagation()}
            >
              {/* ── Car image area ── */}
              {isWindowTint && vehicleImgs ? (
                <div style={{ background: '#111113', overflow: 'hidden', maxHeight: '280px' }}>
                  <img
                    src={vehicleImgs}
                    alt={svc.title}
                    style={{ width: '100%', display: 'block', height: 'auto' }}
                  />
                </div>
              ) : svcImage ? (
                <div style={{ height: '185px', overflow: 'hidden', background: '#111113' }}>
                  <img src={svcImage} alt={svc.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ) : null}

              {/* ── Dark content area ── */}
              <div style={{ background: '#111113', padding: '14px 16px 14px' }}>
                <h3 style={{ color: '#ffffff', fontSize: '22px', fontWeight: 900, textAlign: 'center', margin: '0 0 3px', letterSpacing: '-0.3px' }}>
                  {svc.title}
                </h3>
                <p style={{ color: '#8e8e93', fontSize: '13px', textAlign: 'center', marginBottom: '10px' }}>
                  {svc.description}
                </p>

                {/* Material info card */}
                {materialLabel && (
                  <div style={{ background: materialBg, border: `1px solid ${materialBorder}`, borderRadius: '14px', padding: '10px 12px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
                      <MaterialIcon className="w-4 h-4 flex-shrink-0" style={{ color: materialIconColor }} strokeWidth={2} />
                      <span style={{ color: materialIconColor, fontSize: '11px', fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                        {materialLabel}
                      </span>
                    </div>
                    <p style={{ color: '#a1a1aa', fontSize: '12px', lineHeight: 1.45, marginBottom: '8px' }}>
                      {materialDesc}
                    </p>
                    {/* Tint shade spectrum strip */}
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ height: '7px', borderRadius: '4px', background: 'linear-gradient(to right, rgba(185,185,185,0.78), rgba(130,130,130,0.88), rgba(72,72,72,0.92), rgba(40,40,40,0.95), rgba(8,8,8,1))', marginBottom: '4px' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8e8e93', fontSize: '10px' }}>50% — Lightest</span>
                        <span style={{ color: '#8e8e93', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }}>SHADE RANGE</span>
                        <span style={{ color: '#8e8e93', fontSize: '10px' }}>5% — Limo</span>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px' }}>
                      {svc.features.map((f, i) => {
                        const IconComp = featureIconMap[f] || Award;
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', background: '#1c1c1e', border: `1px solid ${isCeramic ? 'rgba(234,179,8,0.20)' : 'rgba(255,255,255,0.10)'}`, borderRadius: '20px', padding: '9px 12px', minHeight: '38px' }}>
                            <IconComp style={{ color: '#eab308', flexShrink: 0, width: '13px', height: '13px' }} strokeWidth={2} />
                            <span style={{ color: '#ffffff', fontSize: '12px', fontWeight: 600, lineHeight: 1.25 }}>{f}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Non-window-tint simple chips */}
                {!materialLabel && svc.features && svc.features.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px', marginBottom: '10px' }}>
                    {svc.features.map((f, i) => (
                      <span key={i} style={{ border: '1px solid rgba(234,179,8,0.45)', background: 'rgba(234,179,8,0.06)', color: '#e4e4e7', fontSize: '12px', fontWeight: 600, textAlign: 'center', padding: '8px 6px', borderRadius: '20px' }}>
                        {f}
                      </span>
                    ))}
                  </div>
                )}

                {/* BOOK NOW */}
                <button
                  onClick={handleBookNow}
                  data-testid="service-book-now-btn"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eab308', color: '#000000', fontWeight: 900, borderRadius: '14px', padding: '14px 20px', border: 'none', cursor: 'pointer', fontSize: '15px', letterSpacing: '0.5px' }}
                >
                  <span>BOOK NOW</span>
                  <span style={{ width: '1px', height: '16px', background: 'rgba(0,0,0,0.25)', margin: '0 14px', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700 }}>{typeof svc.price === 'number' ? <DiscountedPrice price={svc.price} category={categoryToDiscountKey[activeCat?.id] || ''} /> : svc.price}</span>
                  <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={2.5} />
                </button>

                {activeCat.services.length > 1 && (
                  <button
                    onClick={() => { setSelectedService(''); setShowServiceModal(true); }}
                    data-testid="category-service-select"
                    style={{ width: '100%', textAlign: 'center', color: '#636366', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer', marginTop: '8px', padding: '4px' }}
                  >
                    Change package
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </section>
  );
};
